import {
  SEARCH_OPTION_SELECTED,
  UPDATE_QUERY,
  APP_LOADED,
  REMOVE_FILTER,
  ADD_FILTER,
  UPDATE_FILTER,
  UPDATE_FILTERS,
  UPDATE_SORT,
  RESET_FILTER,
  RESET_FILTERS,
  RESTORE_FILTERS,
  UPDATE_PAGE,
  RESET_PAGE,
  UPDATE_SETTINGS,
  UPDATE_SEARCH_RESULTS_PAGE,
} from "./actionTypes";

import { requestFilters } from "../filters/actions";

import {
  getCurrentPage,
  getInitialQuery,
  getSelectedSearchOptionKey,
  isFilterSelected,
} from "./selectors";

import {
  getAllFilters,
  getDefaultSearchOptionKey,
  getDefaultSortOption,
  getDependentFilters,
  getHiddenFilterKeys,
} from "../filters/selectors";

import { getSorting } from "../content/selectors";
import { selectionTypes } from "../filters/selectionTypes";
import { parse } from "../api/queryString";
import { searchConfig } from "./searchConfig";
import { parseQuery } from "./searchQuery";
import SearchRoute from "./SearchRoute";
import SimpleUrl from "./SimpleUrl";

export const appLoaded = (location, locationChangeAction) => dispatch => {
  const { pageUrl = "/search", routes, defaultUrl } = dispatch(
    setSearchSettings(),
  );
  let initialQuery = {};
  const simpleUrl = new SimpleUrl(routes, pageUrl, defaultUrl);
  const simpleUrlRoutes = simpleUrl.getRoutes();
  const route = new SearchRoute(pageUrl, simpleUrlRoutes);
  if (route.getMatch()) {
    initialQuery = route.getInitialQuery();
  }
  const { search } = location;
  initialQuery = { ...initialQuery, ...parse(search) };
  dispatch({
    type: APP_LOADED,
    payload: { query: initialQuery },
  });

  const back = locationChangeAction === "POP";
  dispatch(requestFilters(back));
};

export const setSearchSettings = () => dispatch => {
  const dataSettings = document.getElementById("root").getAttribute("data-settings");
  const usageType = document.getElementById('PersonalBusinessToggle')?.checked === true ? 'BCH' : 'PCH';
  const payload = dataSettings ? { ...JSON.parse(dataSettings), usageType: usageType } : {};
  dispatch({
    type: UPDATE_SETTINGS,
    payload,
  });
  return payload;
};

export const selectSearchOption = (searchBy, keepFilters = false, isInitialQuery = false) => (
  dispatch,
  getState,
) => {
  const state = getState();
  const currentOption = getSelectedSearchOptionKey(state);
  if (searchBy === currentOption) return;
  if (!keepFilters) {
    if (state.search.savedQuery && state.search.savedQuery.type === searchBy) {
      dispatch(
        restoreFilters({
          updateQueryString: false,
          requestSearchResults: true,
          resetPagination: true,
        }),
      );
    } else {
      dispatch(
        resetFilters({
          updateQueryString: true,
          requestSearchResults: state.search.query.filters.length > 0,
        }),
      );
    }
  }

  dispatch({
    type: SEARCH_OPTION_SELECTED,
    payload: searchBy,
    meta: {
      updateQueryString: true,
      resetPagination: !keepFilters || isInitialQuery,
      replaceState: isInitialQuery,
    },
  });
};

export const setInitialQuery = () => (dispatch, getState) => {
  const state = getState();
  const allFilters = getAllFilters(state);
  const initialQuery = getInitialQuery(state);
  const sorting = getSorting(state);

  const filters = allFilters.map(f => f.selectionType !== selectionTypes.range ? f : f.options);
  const flattened = [].concat(...filters);
  const filterKeys = flattened.map(f => f.key);
  const searchResultsPage = state.search.query.searchResultsPage || 1;
  const updatedQuery = searchResultsPage > 1 ? { ...initialQuery, page: searchResultsPage, searchResultsPage } : initialQuery;
  const query = parseQuery(updatedQuery, sorting, filterKeys);

  dispatch({
    type: UPDATE_QUERY,
    payload: query,
  });

  const searchBy = initialQuery.searchBy || getDefaultSearchOptionKey(state);
  dispatch(selectSearchOption(searchBy, true, true));
};

export const addFilter = payload => dispatch => {
  dispatch(handleFilterChange(payload));

  dispatch({
    type: ADD_FILTER,
    payload: payload,
    meta: searchConfig.defaultOptions,
  });
};

export const removeFilter = payload => dispatch => {
  dispatch(handleFilterChange(payload));

  dispatch({
    type: REMOVE_FILTER,
    payload: payload,
    meta: searchConfig.defaultOptions,
  });
};

export const resetFilter = (payload, options = {}) => dispatch => {
  dispatch(handleFilterChange(payload));
  dispatch({
    type: RESET_FILTER,
    payload: payload.key,
    meta: mergeOptions(options),
  });
};

const resetFilters = (options = {}) => dispatch => {
  dispatch(updateSearchResultsPage(1));
  dispatch({
    type: RESET_FILTERS,
    meta: mergeOptions(options),
  });
};

const restoreFilters = (options = {}) => dispatch => {
  dispatch({
    type: RESTORE_FILTERS,
    meta: mergeOptions(options),
  });
};

export const updateFilter = payload => dispatch => {
  dispatch(handleFilterChange(payload));
  dispatch({
    type: UPDATE_FILTER,
    payload: payload,
    meta: searchConfig.defaultOptions,
  });
};

export const updateFilters = payload => dispatch => {
  dispatch(handleFilterChange(payload));

  dispatch({
    type: UPDATE_FILTERS,
    payload: payload,
    meta: searchConfig.defaultOptions,
  });
};

export const updatePage = (payload, options = {}) => dispatch => {
  dispatch({
    type: UPDATE_PAGE,
    payload: payload.page,
    meta: mergeOptions(options),
  });
};

export const updateSearchResultsPage = payload => dispatch => {
  dispatch({
    type: UPDATE_SEARCH_RESULTS_PAGE,
    payload,
  })
}

const resetPage = (options = searchConfig.updateQueryString) => (dispatch, getState) => {
  const currentPage = getCurrentPage(getState());

  if (currentPage !== 1) {
    dispatch({
      type: RESET_PAGE,
      meta: options,
    });
  }
};

export const updateSort = (payload, options = {}) => (dispatch, getState) => {
  const defaultSort = getDefaultSortOption(getState());

  const keyValue = payload.value === defaultSort ? null : payload;
  dispatch(resetPage());
  dispatch({
    type: UPDATE_SORT,
    payload: keyValue,
    meta: mergeOptions(options),
  });
};

const mergeOptions = options => ({ ...searchConfig.defaultOptions, ...options });

const removeDependentFilters = (payload, getState, dispatch) => {
  const state = getState();
  const dependentFilters = getDependentFilters(state);

  const _hasFilter = isFilterSelected(state);

  if (payload.key in dependentFilters) {
    const keysToRemove = dependentFilters[payload.key];

    keysToRemove
      .filter(key => _hasFilter(key))
      .forEach(key => {
        dispatch(resetFilter({ key }, searchConfig.updateQueryString));
      });
  }
};

const removeHiddenFilters = (dispatch, getState) => {
  const state = getState();

  const _hasFilter = isFilterSelected(state);

  const hiddenFilters = getHiddenFilterKeys(state);

  hiddenFilters
    .filter(key => _hasFilter(key))
    .forEach(key => {
      dispatch({
        type: RESET_FILTER,
        payload: key,
      });
    });
};


const handleFilterChange = (payload) => (dispatch, getState) => {
  const state = getState();
  dispatch(resetPage());
  const searchResultPage = state.search.query.page;
  if (searchResultPage > 1) {
    dispatch(updateSearchResultsPage(searchResultPage));
  }
  removeHiddenFilters(dispatch, getState);

  removeDependentFilters(payload, getState, dispatch);
};
